import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, ChevronDown, Moon } from "lucide-react";
import { apiInstance as api } from "../../../hooks/useApi";
import { useDashTheme } from "../hooks/useDashTheme";
import { Pagination, EmptyState } from "./DataTable";
import { Shimmer } from "./StatCard";

const FILTERS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "7 days" },
  { key: "last14", label: "14 days" },
  { key: "month", label: "Month" },
];

const fmtMs = (ms) => {
  if (!ms || ms <= 0) return "0m";
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + "m" : ""}`.trim() : `${m}m`;
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
// Date only — gap-fill rows carry a query window, not a real idle span.
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) : "—");
// A multi-day filter (7/14/month) folds several gap-fill days into ONE row, so
// label by the day count from idle_note instead of implying a single day.
const gapFillLabel = (log) => {
  const days = Number(String(log?.idle_note || "").match(/\((\d+) day/)?.[1]) || 0;
  if (days > 1) return `${fmtDay(log.startTime)} – ${fmtDay(log.endTime)} · ${days} days total`;
  return `${fmtDay(log.startTime)} · full day total`;
};

// V2-native idle logs modal — matches the DashboardV2 design language,
// hits the same /api/v2/tracking/idle-logs endpoint as the legacy modal.
export function IdleLogsModal({ isOpen, onClose, userId, memberName }) {
  const t = useDashTheme();
  const [filter, setFilter] = React.useState("today");
  const [page, setPage] = React.useState(1);
  const [logs, setLogs] = React.useState([]);
  const [meta, setMeta] = React.useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const abortRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setFilter("today"); setPage(1); setLogs([]);
  }, [isOpen, userId]);

  React.useEffect(() => { setPage(1); }, [filter]);

  React.useEffect(() => {
    if (!isOpen) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true); setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ filter, page });
        if (userId) params.append("userId", userId);
        const res = await api.get(`/api/v2/tracking/idle-logs?${params.toString()}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (res.data.success) {
          const d = res.data.data;
          setLogs(d.logs || []);
          setMeta({ total: d.total || 0, totalPages: d.totalPages || 1 });
        } else setError("Failed to load idle logs.");
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") return;
        setError(err.response?.data?.error?.message || "Failed to load idle logs.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [isOpen, filter, page, userId]);

  const totalMs = logs.reduce((s, l) => s + (l.idleTime || 0), 0);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ position: "absolute", inset: 0, background: "rgba(8,10,18,0.55)", backdropFilter: "blur(4px)" }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={t.motion.spring.snappy}
            style={{
              position: "relative", width: "100%", maxWidth: 640, maxHeight: "85vh", display: "flex", flexDirection: "column",
              background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.lg,
              boxShadow: t.shadow.lg, overflow: "hidden", fontFamily: t.font.family,
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: 10, background: t.status.warningSoft, color: t.status.warning }}>
                  <Moon size={18} />
                </span>
                <div>
                  <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>Idle time logs</div>
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                    {memberName || "Team member"} · {fmtMs(totalMs)} idle{meta.total ? ` · ${meta.total} entries` : ""}
                  </div>
                </div>
              </div>
              <button onClick={onClose} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "none", background: t.c.surfaceMuted, color: t.c.textSecondary, cursor: "pointer" }}>
                <X size={16} />
              </button>
            </div>

            {/* Filter chips */}
            <div style={{ display: "flex", gap: 6, padding: `${t.s[3]}px ${t.s[5]}px`, borderBottom: `1px solid ${t.c.border}`, flexWrap: "wrap" }}>
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button key={f.key} onClick={() => setFilter(f.key)}
                    style={{
                      height: 30, padding: "0 12px", borderRadius: 99, cursor: "pointer",
                      border: `1px solid ${active ? "transparent" : t.c.border}`,
                      background: active ? t.c.primary : "transparent",
                      color: active ? "#fff" : t.c.textSecondary,
                      fontSize: t.font.size.sm, fontWeight: 500, fontFamily: t.font.family, transition: "all 140ms",
                    }}>{f.label}</button>
                );
              })}
            </div>

            {/* Body */}
            <div style={{ overflowY: "auto", padding: t.s[4], display: "flex", flexDirection: "column", gap: t.s[2], flex: 1 }}>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} style={{ padding: t.s[3], borderRadius: t.r.md, border: `1px solid ${t.c.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
                    <Shimmer w="40%" h={12} /><Shimmer w="70%" h={10} />
                  </div>
                ))
              ) : error ? (
                <EmptyState icon={<Clock size={22} />} title="Couldn’t load logs" description={error} />
              ) : logs.length === 0 ? (
                <EmptyState icon={<Moon size={22} />} title="No idle time" description="No idle periods recorded for this range." />
              ) : (
                logs.map((log, i) => <IdleRow key={log._id || i} log={log} t={t} index={i} />)
              )}
            </div>

            {/* Footer pagination */}
            {meta.totalPages > 1 && (
              <Pagination page={page} pages={meta.totalPages} total={meta.total} pageLabel="entries" onChange={setPage} />
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function IdleRow({ log, t, index }) {
  const [open, setOpen] = React.useState(false);
  const events = log.idleEvents || [];
  const multi = events.length > 1;
  const note = events.length === 1 ? (events[0].idle_note || log.idle_note || "No note") : (multi ? null : (log.idle_note || "No note"));

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.2) }}
      style={{ borderRadius: t.r.md, border: `1px solid ${t.c.border}`, overflow: "hidden", background: t.c.surfaceMuted }}
    >
      <div
        onClick={multi ? () => setOpen((v) => !v) : undefined}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: `${t.s[3]}px ${t.s[4]}px`, cursor: multi ? "pointer" : "default" }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: t.status.warningSoft, color: t.status.warning, flexShrink: 0 }}>
          <Clock size={15} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: t.font.size.md, color: t.c.text, fontWeight: 500 }}>
            {log.source === "stealth_gapfill" ? (
              <span style={{ color: t.c.textTertiary, fontWeight: 400 }}>{gapFillLabel(log)}</span>
            ) : (
              <>
                {fmtTime(log.startTime)} <span style={{ color: t.c.textTertiary, fontWeight: 400 }}>→ {fmtTime(log.endTime)}</span>
              </>
            )}
          </div>
          {note && <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{note}</div>}
          {multi && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>{events.length} idle events</div>}
        </div>
        <span style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.status.warning, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtMs(log.idleTime)}</span>
        {multi && <ChevronDown size={16} style={{ color: t.c.textTertiary, transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms", flexShrink: 0 }} />}
      </div>

      <AnimatePresence>
        {open && multi && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
            style={{ borderTop: `1px solid ${t.c.border}`, background: t.c.surface }}>
            {events.map((ev, j) => (
              <div key={j} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: `${t.s[2]}px ${t.s[4]}px`, borderBottom: j < events.length - 1 ? `1px solid ${t.c.border}` : "none" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>{fmtTime(ev.idleStartTime)} → {fmtTime(ev.idleEndTime)}</div>
                  {ev.idle_note && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 1 }}>{ev.idle_note}</div>}
                </div>
                <span style={{ fontSize: t.font.size.sm, fontWeight: 600, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{fmtMs(ev.idleTimeMs)}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
